/*
 * LEO & MILO — TEEINBLUE AUTO SCROLL
 *
 * Komplett unabhängig von teeinblue-ai-progress.js.
 *
 * Ablauf:
 * 1. Kunde lädt ein Bild bei Teeinblue hoch.
 * 2. Script merkt sich: neue Personalisierung läuft.
 * 3. Script erkennt den Teeinblue Upload-/AI-Zustand.
 * 4. Sobald dieser Zustand wieder beendet ist,
 *    wartet das Script kurz.
 * 5. Danach scrollt die Seite weich zur Produktvorschau.
 *
 * Desktop + Mobile
 * Funktioniert auch vom Footer.
 */

(function () {
  'use strict';


  /* ============================================================
     EINSTELLUNGEN
  ============================================================ */

  const DESKTOP_OFFSET = 110;

  const MOBILE_OFFSET = 75;

  /*
   * Nach Ende der Generierung kurz warten,
   * damit Teeinblue das finale Design einsetzen kann.
   */
  const FINISH_DELAY = 800;

  /*
   * Zusätzliche Statusprüfung.
   */
  const CHECK_INTERVAL = 150;

  /*
   * Nach 60 Sekunden Vorgang zurücksetzen,
   * falls Teeinblue wider Erwarten nie fertig meldet.
   */
  const MAX_WAIT = 60000;


  /* ============================================================
     STATUS
  ============================================================ */

  let armed = false;

  let busyWasSeen = false;

  let uploadStartedAt = 0;

  let finishTimer = null;

  let lastScrollAt = 0;

  let observerScheduled = false;


  /* ============================================================
     SICHTBARKEIT
  ============================================================ */

  function isVisible(element) {

    if (!element) {
      return false;
    }


    const rect =
      element.getBoundingClientRect();


    if (
      rect.width <= 0 ||
      rect.height <= 0
    ) {
      return false;
    }


    const style =
      window.getComputedStyle(
        element
      );


    if (
      style.display === 'none'
    ) {
      return false;
    }


    if (
      style.visibility === 'hidden'
    ) {
      return false;
    }


    if (
      Number(style.opacity) === 0
    ) {
      return false;
    }


    return true;
  }


  /* ============================================================
     PRÜFEN, OB ES EIN TEEINBLUE FILE INPUT IST
  ============================================================ */

  function isTeeinblueFileInput(input) {

    if (
      !(input instanceof HTMLInputElement)
    ) {
      return false;
    }


    if (
      input.type !== 'file'
    ) {
      return false;
    }


    /*
     * Teeinblue befindet sich normalerweise innerhalb
     * eines Elements mit tee-* Klasse oder ID.
     */
    const teeParent =
      input.closest(
        [
          '[class*="tee-"]',
          '[id*="tee-"]',
          '[class*="teeinblue"]',
          '[id*="teeinblue"]',
          '#tee-artwork-form'
        ].join(',')
      );


    /*
     * Falls Teeinblue das Input außerhalb seines normalen
     * Wrappers rendert, akzeptieren wir zusätzlich Inputs,
     * wenn auf der Seite generell Teeinblue vorhanden ist.
     */
    const teeExists =
      document.querySelector(
        [
          '.tee-photo',
          '.tee-photo-head',
          '.tee-gallery',
          '.tee-printarea--artwork',
          '#tee-artwork-form'
        ].join(',')
      );


    return !!(
      teeParent ||
      teeExists
    );
  }


  /* ============================================================
     PROGRESSWERT AUSLESEN
  ============================================================ */

  function getProgressValue() {

    const bars =
      document.querySelectorAll(
        '.tee-progress-bar[aria-valuenow]'
      );


    let highestValue = null;


    bars.forEach(
      function (bar) {

        const value =
          parseInt(
            bar.getAttribute(
              'aria-valuenow'
            ),
            10
          );


        if (
          Number.isNaN(value)
        ) {
          return;
        }


        if (
          highestValue === null ||
          value > highestValue
        ) {

          highestValue =
            value;

        }

      }
    );


    return highestValue;
  }


  /* ============================================================
     TEEINBLUE ARBEITET?
  ============================================================ */

  function isTeeinblueBusy() {

    /*
     * Wichtigster Indikator:
     * Teeinblue Uploading Container.
     */
    const uploadingElements =
      document.querySelectorAll(
        '.tee-photo-uploading'
      );


    for (
      const element
      of uploadingElements
    ) {

      if (
        isVisible(element)
      ) {
        return true;
      }

    }


    /*
     * Progress-Element.
     */
    const progressElements =
      document.querySelectorAll(
        '.tee-photo-uploading .tee-progress'
      );


    for (
      const progress
      of progressElements
    ) {

      if (
        isVisible(progress)
      ) {
        return true;
      }

    }


    /*
     * Generische Teeinblue Loading-Klassen.
     */
    const genericBusyElements =
      document.querySelectorAll(
        [
          '[class*="tee-"][class*="loading"]',
          '[class*="tee-"][class*="uploading"]',
          '[class*="tee-"][class*="processing"]',
          '[class*="tee-"][class*="generating"]'
        ].join(',')
      );


    for (
      const element
      of genericBusyElements
    ) {

      if (
        isVisible(element)
      ) {
        return true;
      }

    }


    return false;
  }


  /* ============================================================
     PRODUKTVORSCHAU FINDEN
  ============================================================ */

  function getPreviewTarget() {

    /*
     * 1.
     * Impact Produktgalerie.
     *
     * Das ist unser bevorzugtes Scrollziel.
     */
    const productGallery =
      document.querySelector(
        '.product-gallery'
      );


    if (
      productGallery &&
      isVisible(productGallery)
    ) {
      return productGallery;
    }


    /*
     * 2.
     * Teeinblue Gallery.
     */
    const teeGallery =
      document.querySelector(
        '.tee-gallery'
      );


    if (
      teeGallery &&
      isVisible(teeGallery)
    ) {
      return teeGallery;
    }


    /*
     * 3.
     * Tatsächliches Teeinblue Artwork.
     */
    const artworks =
      Array.from(
        document.querySelectorAll(
          '.tee-printarea--artwork'
        )
      );


    const artwork =
      artworks.find(
        function (element) {

          if (
            element.closest(
              '.vm--container'
            )
          ) {
            return false;
          }


          return isVisible(
            element
          );

        }
      );


    if (artwork) {
      return artwork;
    }


    /*
     * 4.
     * Main Product Section.
     */
    const mainProduct =
      document.querySelector(
        '.shopify-section[id*="main-product"]'
      );


    if (mainProduct) {
      return mainProduct;
    }


    /*
     * 5.
     * Weitere Impact Fallbacks.
     */
    const product =
      document.querySelector(
        '.product'
      );


    if (product) {
      return product;
    }


    const productInfo =
      document.querySelector(
        '.product-info'
      );


    if (productInfo) {
      return productInfo;
    }


    return null;
  }


  /* ============================================================
     SCROLL DURCHFÜHREN
  ============================================================ */

  function scrollToPreview() {

    const target =
      getPreviewTarget();


    if (!target) {

      console.warn(
        '[Leo & Milo Auto Scroll] Vorschau nicht gefunden.'
      );

      return;
    }


    const mobile =
      window.matchMedia(
        '(max-width: 749px)'
      ).matches;


    const offset =
      mobile
        ? MOBILE_OFFSET
        : DESKTOP_OFFSET;


    /*
     * Absolute Position des Elements auf der Seite.
     *
     * Dadurch funktioniert der Scroll auch,
     * wenn der Kunde gerade ganz unten im Footer ist.
     */
    const rect =
      target.getBoundingClientRect();


    const targetTop =
      window.scrollY +
      rect.top -
      offset;


    window.scrollTo({
      top:
        Math.max(
          0,
          targetTop
        ),

      left:
        0,

      behavior:
        'smooth'
    });


    lastScrollAt =
      Date.now();
  }


  /* ============================================================
     SCROLL PLANEN
  ============================================================ */

  function scheduleFinishScroll() {

    /*
     * Vorgang wurde bereits beendet.
     */
    if (!armed) {
      return;
    }


    /*
     * Doppeltes Scrollen verhindern.
     */
    if (
      Date.now() -
      lastScrollAt <
      1500
    ) {

      armed = false;

      busyWasSeen = false;

      return;
    }


    armed = false;

    busyWasSeen = false;


    if (finishTimer) {

      clearTimeout(
        finishTimer
      );

    }


    finishTimer =
      window.setTimeout(
        function () {

          /*
           * Zwei Frames warten,
           * damit Browser + Teeinblue Layout fertig sind.
           */
          requestAnimationFrame(
            function () {

              requestAnimationFrame(
                function () {

                  scrollToPreview();

                }
              );

            }
          );

        },
        FINISH_DELAY
      );
  }


  /* ============================================================
     STATUS PRÜFEN
  ============================================================ */

  function checkState() {

    if (!armed) {
      return;
    }


    /*
     * Sicherheits-Timeout.
     */
    if (
      Date.now() -
      uploadStartedAt >
      MAX_WAIT
    ) {

      console.warn(
        '[Leo & Milo Auto Scroll] Timeout.'
      );


      armed = false;

      busyWasSeen = false;


      return;
    }


    const progressValue =
      getProgressValue();


    const busy =
      isTeeinblueBusy();


    /*
     * Variante A:
     *
     * Wir sehen einen echten Teeinblue Busy-Zustand.
     */
    if (busy) {

      busyWasSeen = true;


      /*
       * Falls Teeinblue sichtbar 100 % meldet,
       * können wir direkt auf Fertig gehen.
       */
      if (
        progressValue !== null &&
        progressValue >= 100
      ) {

        scheduleFinishScroll();

      }


      return;
    }


    /*
     * Variante B:
     *
     * Teeinblue war vorher busy und ist es jetzt
     * nicht mehr.
     *
     * Das ist unser zuverlässiger Fallback.
     */
    if (
      busyWasSeen &&
      !busy
    ) {

      scheduleFinishScroll();

    }
  }


  /* ============================================================
     NEUEN UPLOAD AKTIVIEREN
  ============================================================ */

  function armAutoScroll() {

    armed = true;

    busyWasSeen = false;

    uploadStartedAt =
      Date.now();


    if (finishTimer) {

      clearTimeout(
        finishTimer
      );

      finishTimer = null;

    }


    /*
     * Mehrere frühe Checks.
     *
     * Teeinblue braucht teilweise einen kurzen Moment,
     * bevor der Uploading-Container erscheint.
     */
    setTimeout(
      checkState,
      25
    );


    setTimeout(
      checkState,
      75
    );


    setTimeout(
      checkState,
      150
    );


    setTimeout(
      checkState,
      300
    );


    setTimeout(
      checkState,
      600
    );


    setTimeout(
      checkState,
      1000
    );
  }


  /* ============================================================
     FILE CHANGE
  ============================================================ */

  document.addEventListener(
    'change',
    function (event) {

      const input =
        event.target;


      if (
        !isTeeinblueFileInput(
          input
        )
      ) {
        return;
      }


      if (
        !input.files ||
        input.files.length === 0
      ) {
        return;
      }


      armAutoScroll();

    },
    true
  );


  /* ============================================================
     FILE INPUT FALLBACK
  ============================================================ */

  document.addEventListener(
    'input',
    function (event) {

      const input =
        event.target;


      if (
        !isTeeinblueFileInput(
          input
        )
      ) {
        return;
      }


      if (
        !input.files ||
        input.files.length === 0
      ) {
        return;
      }


      if (!armed) {

        armAutoScroll();

      }

    },
    true
  );


  /* ============================================================
     MUTATION OBSERVER
  ============================================================ */

  const observer =
    new MutationObserver(
      function () {

        if (
          !armed ||
          observerScheduled
        ) {
          return;
        }


        observerScheduled = true;


        requestAnimationFrame(
          function () {

            observerScheduled = false;

            checkState();

          }
        );

      }
    );


  function startObserver() {

    if (!document.body) {
      return;
    }


    observer.observe(
      document.body,
      {

        childList: true,

        subtree: true,

        attributes: true,

        characterData: true,

        attributeFilter: [
          'class',
          'style',
          'aria-valuenow',
          'src'
        ]

      }
    );
  }


  /* ============================================================
     POLLING FALLBACK
  ============================================================ */

  window.setInterval(
    function () {

      if (armed) {

        checkState();

      }

    },
    CHECK_INTERVAL
  );


  /* ============================================================
     DEBUG
  ============================================================ */

  /*
   * TEST 1:
   *
   * Ganz nach unten scrollen und Console:
   *
   * LM_SCROLL_TO_TEEINBLUE()
   *
   * → muss sofort smooth zur Vorschau scrollen.
   */

  window.LM_SCROLL_TO_TEEINBLUE =
    function () {

      scrollToPreview();

    };


  /*
   * TEST 2:
   *
   * Während einer Generierung:
   *
   * LM_TEEINBLUE_SCROLL_STATUS()
   */

  window.LM_TEEINBLUE_SCROLL_STATUS =
    function () {

      const status = {

        armed:
          armed,

        busyWasSeen:
          busyWasSeen,

        busyNow:
          isTeeinblueBusy(),

        progress:
          getProgressValue(),

        uploadStartedAt:
          uploadStartedAt,

        secondsSinceUpload:
          uploadStartedAt
            ? Math.round(
                (
                  Date.now() -
                  uploadStartedAt
                ) / 100
              ) / 10
            : null

      };


      console.log(
        '[Leo & Milo Auto Scroll]',
        status
      );


      return status;
    };


  /* ============================================================
     INIT
  ============================================================ */

  function init() {

    startObserver();

  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );

  } else {

    init();

  }

})();